import { createContext, forwardRef, useCallback, useContext, useImperativeHandle, useState } from 'react';
import { MessageTestingOverlay } from './Overlay';
import { MessageTestingResultsPanel } from './ResultsPanel';

// ─── Context ────────────────────────────────────────────────────────────────

export interface MessageTestingResultsState {
  activeTestRunId: number | null;
  openResultsPanel: (runId: number) => void;
  closeResultsPanel: () => void;
}

export const MessageTestingResultsContext = createContext<MessageTestingResultsState | null>(null);
MessageTestingResultsContext.displayName = 'MessageTestingResultsContext';

// ─── Container ──────────────────────────────────────────────────────────────

interface MessageTestingResultsContainerProps {
  /** Which side of the viewport the overlay appears on. */
  overlaySide: 'left' | 'right';
  children: React.ReactNode;
}

/** Handle exposed by the container via ref for imperative access. */
export type MessageTestingResultsHandle = MessageTestingResultsState;

/**
 * Provides `MessageTestingResultsContext` and renders the floating
 * MessageTestingResultsPanel overlay when a test run is active.
 *
 * The panel self-fetches its data (messages, segments, brand, objective) from the
 * `GET /message-testing/:id` endpoint, so callers only need to provide
 * the `runId` when opening the panel.
 *
 * Use `ref` to get an imperative handle for calling `openResultsPanel` /
 * `closeResultsPanel` from outside the provider tree.
 */
export const MessageTestingResultsContainer = forwardRef<
  MessageTestingResultsHandle,
  MessageTestingResultsContainerProps
>(function MessageTestingResultsContainer({ overlaySide, children }, ref): React.JSX.Element {
  const [activeTestRunId, setActiveTestRunId] = useState<number | null>(null);

  const openResultsPanel = useCallback((runId: number) => {
    setActiveTestRunId(runId);
  }, []);

  const closeResultsPanel = useCallback(() => {
    setActiveTestRunId(null);
  }, []);

  const ctx: MessageTestingResultsState = {
    activeTestRunId,
    openResultsPanel,
    closeResultsPanel,
  };

  useImperativeHandle(ref, () => ctx, [activeTestRunId, openResultsPanel, closeResultsPanel]);

  return (
    <MessageTestingResultsContext.Provider value={ctx}>
      {children}
      {activeTestRunId && (
        <MessageTestingOverlay $side={overlaySide}>
          <MessageTestingResultsPanel runId={activeTestRunId} onClose={closeResultsPanel} />
        </MessageTestingOverlay>
      )}
    </MessageTestingResultsContext.Provider>
  );
});

/**
 * Convenience hook to access the MessageTestingResults context from a descendant.
 */
export function useMessageTestingResultsContext(): MessageTestingResultsState | null {
  return useContext(MessageTestingResultsContext);
}
